import React from 'react';
import { FlexDiv, TextDefault } from './ui';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
    ensureYesNoListActions,
    getYesNoComponent,
    getYesNoScore,
    removeYesNoSubAction,
    setYesNoComponent,
    setYesNoScore,
} from './questionActionsEditor';
import { ScoringComponentSelect } from './scoringComponentSelect';

const paperSx = {
    p: 2,
    mb: 2,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'grey.50',
    boxShadow: 1,
};

export function YesNoListBlockEditor({
    selectedBlock,
    question,
    scoringComponentOptions,
    onPatchQuestion,
    onUpdateBlock,
}) {
    if (selectedBlock.type !== 'yes-no-list') return null;

    const questions = selectedBlock.questions || [];
    const aid = selectedBlock.actionId;
    const minQuestions = 1;

    return (
        <FlexDiv column gap={1}>
            <TextDefault bold>Yes / No questions and scores</TextDefault>
            <TextDefault lighter>
                Each card is one sub-question. Scores live at
                actions[blockActionId][subActionId].values (yes / no).
            </TextDefault>
            {questions.map((q, index) => {
                const subId = q.actionId;
                const canRemove = questions.length > minQuestions;
                return (
                    <Paper
                        key={`${selectedBlock.id}-yn-${index}`}
                        variant="outlined"
                        sx={paperSx}
                    >
                        <Stack spacing={2}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="space-between"
                            >
                                <TextDefault bold>
                                    Question {index + 1}
                                </TextDefault>
                                <IconButton
                                    size="small"
                                    disabled={!canRemove || !onPatchQuestion}
                                    aria-label="Remove question"
                                    onClick={() => {
                                        if (!onPatchQuestion) return;
                                        onPatchQuestion((prev) => {
                                            const bid = selectedBlock.id;
                                            const block = prev.ui.blocks.find(
                                                (b) => b.id === bid
                                            );
                                            if (!block) return prev;
                                            const sid = block.questions[index]
                                                ?.actionId;
                                            const newQuestions =
                                                block.questions.filter(
                                                    (_, i) => i !== index
                                                );
                                            let actions = removeYesNoSubAction(
                                                prev.actions || {},
                                                block.actionId,
                                                sid
                                            );
                                            const blocks = prev.ui.blocks.map(
                                                (b) =>
                                                    b.id === bid
                                                        ? {
                                                              ...b,
                                                              questions:
                                                                  newQuestions,
                                                          }
                                                        : b
                                            );
                                            return {
                                                ...prev,
                                                ui: { ...prev.ui, blocks },
                                                actions,
                                            };
                                        });
                                    }}
                                >
                                    <DeleteOutlineIcon fontSize="small" />
                                </IconButton>
                            </Stack>
                            <TextField
                                size="small"
                                label="Question text"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={q.text || ''}
                                onChange={(e) => {
                                    const newQuestions = [...questions];
                                    newQuestions[index] = {
                                        ...q,
                                        text: e.target.value,
                                    };
                                    onUpdateBlock({ questions: newQuestions });
                                }}
                            />
                            <TextField
                                size="small"
                                label="Sub actionId"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={subId || ''}
                                onChange={(e) => {
                                    const newQuestions = [...questions];
                                    newQuestions[index] = {
                                        ...q,
                                        actionId: e.target.value,
                                        type: q.type || 'yes-no-question',
                                    };
                                    onUpdateBlock({ questions: newQuestions });
                                }}
                                placeholder="e.g. admin_password"
                            />
                            {subId && aid && onPatchQuestion ? (
                                <Stack spacing={2}>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={2}
                                        alignItems="flex-start"
                                    >
                                        <TextField
                                            label="Yes score"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ step: 'any' }}
                                            value={getYesNoScore(
                                                question.actions,
                                                aid,
                                                subId,
                                                'yes'
                                            )}
                                            onChange={(e) => {
                                                onPatchQuestion((prev) => ({
                                                    ...prev,
                                                    actions: setYesNoScore(
                                                        prev.actions || {},
                                                        aid,
                                                        subId,
                                                        'yes',
                                                        e.target.value,
                                                        prev
                                                    ),
                                                }));
                                            }}
                                        />
                                        <Box sx={{ width: '100%', maxWidth: 280 }}>
                                            <ScoringComponentSelect
                                                componentOptions={
                                                    scoringComponentOptions
                                                }
                                                label="Yes · component"
                                                value={getYesNoComponent(
                                                    question.actions,
                                                    aid,
                                                    subId,
                                                    'yes',
                                                    question
                                                )}
                                                onChange={(comp) => {
                                                    onPatchQuestion((prev) => ({
                                                        ...prev,
                                                        actions:
                                                            setYesNoComponent(
                                                                prev.actions ||
                                                                    {},
                                                                aid,
                                                                subId,
                                                                'yes',
                                                                comp,
                                                                prev
                                                            ),
                                                    }));
                                                }}
                                            />
                                        </Box>
                                    </Stack>
                                    <Stack
                                        direction={{ xs: 'column', sm: 'row' }}
                                        spacing={2}
                                        alignItems="flex-start"
                                    >
                                        <TextField
                                            label="No score"
                                            type="number"
                                            size="small"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            inputProps={{ step: 'any' }}
                                            value={getYesNoScore(
                                                question.actions,
                                                aid,
                                                subId,
                                                'no'
                                            )}
                                            onChange={(e) => {
                                                onPatchQuestion((prev) => ({
                                                    ...prev,
                                                    actions: setYesNoScore(
                                                        prev.actions || {},
                                                        aid,
                                                        subId,
                                                        'no',
                                                        e.target.value,
                                                        prev
                                                    ),
                                                }));
                                            }}
                                        />
                                        <Box sx={{ width: '100%', maxWidth: 280 }}>
                                            <ScoringComponentSelect
                                                componentOptions={
                                                    scoringComponentOptions
                                                }
                                                label="No · component"
                                                value={getYesNoComponent(
                                                    question.actions,
                                                    aid,
                                                    subId,
                                                    'no',
                                                    question
                                                )}
                                                onChange={(comp) => {
                                                    onPatchQuestion((prev) => ({
                                                        ...prev,
                                                        actions:
                                                            setYesNoComponent(
                                                                prev.actions ||
                                                                    {},
                                                                aid,
                                                                subId,
                                                                'no',
                                                                comp,
                                                                prev
                                                            ),
                                                    }));
                                                }}
                                            />
                                        </Box>
                                    </Stack>
                                </Stack>
                            ) : (
                                <TextDefault lighter>
                                    Set sub actionId to edit scores.
                                </TextDefault>
                            )}
                        </Stack>
                    </Paper>
                );
            })}
            {onPatchQuestion && aid ? (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
                    <IconButton
                        size="small"
                        color="primary"
                        aria-label="Add question"
                        onClick={() => {
                            onPatchQuestion((prev) => {
                                const bid = selectedBlock.id;
                                const block = prev.ui.blocks.find(
                                    (b) => b.id === bid
                                );
                                if (!block?.actionId) return prev;
                                const stamp = Date.now();
                                const newSubId = `yn_${stamp}_${Math.random().toString(36).slice(2, 6)}`;
                                const newQ = {
                                    text: '',
                                    type: 'yes-no-question',
                                    actionId: newSubId,
                                };
                                const newQuestions = [
                                    ...(block.questions || []),
                                    newQ,
                                ];
                                const nextActions = ensureYesNoListActions(
                                    prev.actions || {},
                                    { ...block, questions: newQuestions },
                                    prev
                                );
                                const blocks = prev.ui.blocks.map((b) =>
                                    b.id === bid
                                        ? { ...b, questions: newQuestions }
                                        : b
                                );
                                return {
                                    ...prev,
                                    ui: { ...prev.ui, blocks },
                                    actions: nextActions,
                                };
                            });
                        }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                    <TextDefault lighter>Add question</TextDefault>
                </Stack>
            ) : null}
        </FlexDiv>
    );
}
